<template>
  <div class="user-info-component" :class="{ 'compact': compact }">
    <template v-if="authStore.isAuthenticated">
      <!-- 头像 -->
      <div class="user-avatar" v-if="showAvatar">
        <Avatar 
          :size="avatarSize" 
          class="premium-avatar"
        >
          {{ displayInitials }}
        </Avatar>
      </div>
      
      <!-- 用户信息 -->
      <div class="user-details" v-if="showDetails">
        <div class="user-name">{{ displayName }}</div>
        <div class="user-meta" v-if="showMeta">
          <span class="user-email">{{ authStore.email }}</span>
          <Tag 
            v-if="showStatus" 
            :color="authStore.user?.userInfo?.isActive ? 'green' : 'red'" 
            size="small"
          >
            {{ authStore.user?.userInfo?.isActive ? '活跃' : '已停用' }}
          </Tag>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="user-actions" v-if="showActions">
        <Dropdown placement="bottom-end" @on-click="handleAction" class="user-dropdown">
          <div class="action-trigger">
            <Icon type="md-person" />
            <Icon type="md-arrow-dropdown" v-if="!compact" class="dropdown-arrow" />
          </div>
          <template #list>
            <DropdownMenu class="premium-dropdown-menu">
              <DropdownItem name="profile">
                <Icon type="md-person" />
                个人资料
              </DropdownItem>
              <DropdownItem name="settings">
                <Icon type="md-settings" />
                账户设置
              </DropdownItem>
              <!-- 管理员显示后台管理 -->
              <DropdownItem name="admin" v-if="isAdmin">
                <Icon type="md-construct" />
                后台管理
              </DropdownItem>
              <!-- 仅非管理员显示意见反馈 -->
              <DropdownItem name="feedback" v-else>
                <Icon type="md-chatbubbles" />
                意见反馈
              </DropdownItem>
              <div class="premium-divider"></div>
              <DropdownItem name="logout" class="logout-item">
                <Icon type="md-log-out" />
                退出登录
              </DropdownItem>
            </DropdownMenu>
          </template>
        </Dropdown>
      </div>
    </template>
    
    <!-- 未登录状态 -->
    <div class="user-login-prompt" v-else>
      <Button type="primary" size="small" @click="router.push('/login')" class="login-btn">
        登录
      </Button>
    </div>

    <!-- 反馈弹窗 -->
    <FeedbackModal v-model="feedbackModalVisible" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Message } from 'view-ui-plus'
import FeedbackModal from '@/components/feedback/FeedbackModal.vue'

interface Props {
  // 是否显示头像
  showAvatar?: boolean
  // 是否显示用户详细信息（姓名、邮箱等）
  showDetails?: boolean
  // 是否显示元数据（邮箱、状态等）
  showMeta?: boolean
  // 是否显示状态标签
  showStatus?: boolean
  // 是否显示操作按钮
  showActions?: boolean
  // 紧凑模式
  compact?: boolean
  // 头像大小
  avatarSize?: number | string
}

const props = withDefaults(defineProps<Props>(), {
  showAvatar: true,
  showDetails: true,
  showMeta: false,
  showStatus: false,
  showActions: true,
  compact: false,
  avatarSize: 32
})

const router = useRouter()
const authStore = useAuthStore()
const feedbackModalVisible = ref(false)

const isAdmin = computed(() => authStore.roles.includes('admin'))

// 显示名称
const displayName = computed(() => {
  return authStore.displayName || authStore.userName || '用户'
})

// 头像初始字符
const displayInitials = computed(() => {
  const name = displayName.value
  if (name.length >= 2) {
    // 如果是中文名，取前两个字符
    if (/[\u4e00-\u9fa5]/.test(name)) {
      return name.slice(-2)
    }
    // 如果是英文名，取首字母
    const words = name.split(' ')
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
  }
  return name.slice(0, 2).toUpperCase()
})

// 处理下拉菜单操作
const handleAction = async (name: string) => {
  switch (name) {
    case 'profile':
      router.push('/account-settings')
      break
      
    case 'settings':
      router.push('/account-settings')
      break

    case 'admin':
      router.push('/admin/feedback-management')
      break

    case 'feedback':
      feedbackModalVisible.value = true
      break
      
    case 'logout':
      try {
        await authStore.logout()
        Message.success('已退出登录')
        router.push('/login')
      } catch (error) {
        Message.error('退出登录失败')
      }
      break
  }
}
</script>

<style scoped>
.user-info-component {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.user-info-component:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.user-info-component.compact {
  gap: 8px;
  padding: 4px 8px;
}

.premium-avatar {
  background: linear-gradient(135deg, #0575e6 0%, #00f260 100%) !important;
  color: white !important;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 242, 96, 0.2);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  color: #f8fafc;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.action-trigger:hover {
  color: #f8fafc;
  background: rgba(255, 255, 255, 0.1);
}

.action-trigger:active {
  color: #00f260;
  background: rgba(0, 242, 96, 0.1);
  transform: scale(0.95);
}

.dropdown-arrow {
  font-size: 14px;
  opacity: 0.7;
}

/* Premium Dropdown Styles */
:deep(.premium-dropdown-menu) {
  background: rgba(30, 41, 59, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

:deep(.ivu-dropdown-item) {
  color: #e2e8f0 !important;
  padding: 10px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  transition: all 0.2s;
}

:deep(.ivu-dropdown-item:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #00f260 !important;
}

:deep(.premium-divider) {
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 6px 0;
}

:deep(.logout-item:hover) {
  color: #ff4d4f !important;
  background: rgba(255, 77, 79, 0.1) !important;
}

.login-btn {
  background: var(--accent-gradient) !important;
  border: none !important;
}

/* 响应式 */
@media (max-width: 768px) {
  .user-info-component {
    padding: 4px;
    background: transparent;
    border: none;
  }
  
  .user-details {
    display: none;
  }
}
</style>